import { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux";
import { routes } from "./routes";
import { isHomeHealthAtRiskOnlyUser } from "../../util/atRiskOverrideHelpers";
import { isHomeHealthExternalUser } from "../../constants/sso";
import { isHomeHealthExternalAllowedRoute } from "./compassusRouteAccess";

interface HomeHealthRouteGuardProps {
  children: ReactNode;
}

export const HomeHealthRouteGuard: FC<HomeHealthRouteGuardProps> = ({
  children,
}) => {
  const location = useLocation();
  const user = useAppSelector((state) => state.auth?.user);
  const patients = useAppSelector((state) => state.homehealth?.patients ?? []);
  const userClinic = useAppSelector((state) => state.clinic?.userClinic);
  const isHomeHealthExternal = isHomeHealthExternalUser(
    user?.groups,
    user?.email
  );

  if (
    isHomeHealthExternal &&
    !isHomeHealthExternalAllowedRoute(location.pathname)
  ) {
    return <Navigate to={routes.visitNotes.path} replace />;
  }

  // VRN home-health restricted view: only the Scheduling, Insights, Vitals
  // AI, IQ/Neo, and Medications modules are hidden for this role; the rest
  // of the side navigation (Inbox, Clinical, Watchlist, Consents & devices,
  // other AI Agents, patient profile, Navigator, settings, ...) remains
  // accessible. See documentation/VRN_HOMEHEALTH_AT_RISK_REVIEW.md.
  if (isHomeHealthAtRiskOnlyUser({ user, clinic: userClinic })) {
    const npSchedulingPath = routes?.npScheduling?.path ?? "/np-scheduling";
    const insightsPath = routes?.Insights?.path ?? "/insights";
    const vitalSyncPath = routes?.vitalSync?.path ?? "/vitals-ai";
    const iqPath = routes?.iq?.path ?? "/iq";
    const neoPath = routes?.neo?.path ?? "/neo";
    const medicationsPath = routes?.medRecAI?.path ?? "/medications";
    const blockedPathPrefixes = [
      npSchedulingPath,
      insightsPath,
      vitalSyncPath,
      iqPath,
      neoPath,
      medicationsPath,
    ];
    const isBlockedPath = blockedPathPrefixes.some((prefix) =>
      location.pathname.startsWith(prefix)
    );
    if (isBlockedPath) {
      return <Navigate to="/navigator" replace />;
    }
    return <>{children}</>;
  }

  // If user has homeHealthId, they should only be able to access patient profile
  if (user?.homeHealthId) {
    // Allow access to patient profile routes
    if (location.pathname.startsWith("/patient-profile")) {
      return <>{children}</>;
    }

    // Redirect to patient profile for all other routes
    // Use the patientId from Redux if available, otherwise fall back to homeHealthId
    const redirectId = Array.isArray(patients)
      ? patients.find((p: any) => p?._id === user.homeHealthId)?.patient_id
      : undefined;

    if (redirectId) {
      return (
        <Navigate
          to={routes.patientProfile.path.replace(
            ":patientId",
            String(redirectId)
          )}
          replace
        />
      );
    }
  }

  // For non-home health users, allow access to all routes
  return <>{children}</>;
};
